import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { UseFormSetError } from 'react-hook-form';
import { DealerForm } from '../components/DealerForm';
import { useDealerQuery } from '../hooks/useDealerQuery';
import { useUpdateDealerMutation } from '../hooks/useUpdateDealerMutation';
import { handleUserMutationError } from '@/features/users/services/userErrorHandler';
import type { DealerFormInput } from '../schemas/dealer.schema';
import { Alert, TextLink } from '@/shared/components';

export function DealerEdit() {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const query = useDealerQuery(id);
    const mutation = useUpdateDealerMutation();
    const [topLevelError, setTopLevelError] = useState<string | null>(null);
    const setErrorRef = useRef<UseFormSetError<DealerFormInput> | null>(null);

    if (query.isLoading) {
        return <p>Loading dealer...</p>;
    }

    if (query.isError || !query.data) {
        return (
            <Alert variant="error">
                <span>Dealer not found or failed to load.</span>
            </Alert>
        );
    }

    const dealer = query.data;

    const handleSubmit = async (payload: DealerFormInput) => {
        setTopLevelError(null);
        try {
            await mutation.mutateAsync({ id, payload });
            navigate(`/admin/dealers/${id}`);
        } catch (err: unknown) {
            const result = handleUserMutationError(err);
            if (result.status === 422 && result.fieldErrors && setErrorRef.current) {
                for (const [field, messages] of Object.entries(result.fieldErrors)) {
                    setErrorRef.current(
                        field as Parameters<UseFormSetError<DealerFormInput>>[0],
                        { type: 'server', message: messages[0] ?? 'Invalid' },
                    );
                }
                return;
            }
            const message = err instanceof Error ? err.message : 'Failed to update dealer';
            setTopLevelError(message);
        }
    };

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to={`/admin/dealers/${id}`} color="secondary" className="text-sm">
                    &larr; Back to {dealer.name}
                </TextLink>
            </div>
            <h1 className="text-2xl font-semibold">Edit {dealer.name}</h1>
            {topLevelError && (
                <Alert variant="error">
                    <span>{topLevelError}</span>
                </Alert>
            )}
            <DealerForm
                mode="edit"
                initialValues={dealer}
                onSubmit={handleSubmit}
                onSetErrorRef={(setError) => { setErrorRef.current = setError as UseFormSetError<DealerFormInput>; }}
                submitLabel="Save changes"
            />
        </section>
    );
}
